import React, { ReactNode } from "react";
import { isFunction, map } from "lodash";
import styled from "styled-components";
import { FontWeight, content, form, transparentize, useTheme } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Bars from "./bars";
import { callRange, getRanges, useBars } from "./data";
import { Placement, RangeData, Serie } from "./model";

interface IProps {
  data: RangeData[];
  locale: Locale;
  tooltip?: (serie: any) => ReactNode;
  interactionSelectRange?: (range: string) => void;
  interactionSelectRangeValue?: (range: string, gender: string) => void;
  hovered: string;
  series: Serie[];
}

const StyledTable = styled.table`
  height: calc(100% - 84px);
  width: 100%;
  max-width: 900px;
  border-spacing: 0;
  border-collapse: collapse;
  margin: 36px auto 24px;
`;

const StyledTd = styled.td<{
  interactive?: boolean;
  placement?: Placement;
  legend?: boolean;
}>`
  text-align: center;
  padding: 0 6px;
  padding-bottom: ${(props) => props.placement === "top" && "6px"};
  padding-top: ${(props) => props.placement === "bottom" && "6px"};
  width: calc(80% / 7);
  border: none;
  height: 39%;
  cursor: ${(props) => props.interactive && "var(--cursor-magic-wand)"};
  position: relative;
  vertical-align: ${(props) => props.placement === "top" && "bottom"};
  vertical-align: ${(props) => props.placement === "bottom" && "top"};
  &:hover {
    background: ${(props) => transparentize(0.85, form(props, "primary"))};
  }
`;

const MiddleAxis = styled(StyledTd)`
  border-style: solid;
  border-width: 1px 0;
  border-color: ${(props) => content(props, "tertiary")};
  font-weight: ${FontWeight.Normal};
  height: 22px;
  vertical-align: middle;
  font-size: 14px;
  color: ${content};
`;

const DemographyChart = ({
  data,
  locale,
  series,
  tooltip,
  interactionSelectRange,
  interactionSelectRangeValue: selectRangeValue,
  hovered,
}: IProps) => {
  const ranges = getRanges(locale);
  const theme = useTheme();
  const [topBars, bottomBars] = useBars(series, ranges, data, theme);

  return (
    <StyledTable>
      <tbody>
        <tr>
          <Bars
            bars={topBars}
            locale={locale}
            onClick={selectRangeValue}
            tooltip={tooltip}
            placement={"top"}
            hovered={hovered}
          />
        </tr>
        <tr>
          {map(ranges, (range) => (
            <MiddleAxis
              key={range.id}
              interactive={isFunction(interactionSelectRange)}
              onClick={callRange(interactionSelectRange, range.id)}
            >
              <span>{range.label}</span>
            </MiddleAxis>
          ))}
        </tr>
        <tr>
          <Bars
            bars={bottomBars}
            locale={locale}
            onClick={selectRangeValue}
            tooltip={tooltip}
            placement={"bottom"}
            hovered={hovered}
          />
        </tr>
      </tbody>
    </StyledTable>
  );
};

export default DemographyChart;
